import React, { useState, useCallback, useEffect, useRef } from 'react'
import { View, StyleSheet, ScrollView, Animated } from 'react-native'
import PropTypes from 'prop-types'
import moment from 'moment'
import { LinearGradient } from 'expo-linear-gradient'

import Txt from '../../../../components/Txt'
import Calendar from '../Calendar'
import EventsModal from '../EventsModal'
import EventCard from '../EventCard'
import SkeletonContent from 'react-native-skeleton-content'
import { FlatList } from 'react-native-gesture-handler'
import { usePlatforms } from '../../../../hooks/usePlatforms'
import { useTheme } from '../../../../theming/ThemeProvider'
import { IconButton } from '../../../../components'

const EventsSection = ({ events, loading }) => {
  const { isDesktop } = usePlatforms()
  const { theme } = useTheme()
  const [openEventsModal, setOpenEventsModal] = useState(false)
  const [selectedDate, setSelectedDate] = useState(moment.utc())
  const [filteredEvents, setFilteredEvents] = useState([])
  const fadeAnim = useRef(new Animated.Value(0)).current
  const styles = useStyles(isDesktop)
  const eventsListMaxHeight = 305
  const eventsScrollRef = useRef()
  const scrollPosition = useRef(0)

  const onScrollDown = useCallback((position) => {
    const eventsScroll = eventsScrollRef.current
    if (eventsScroll) {
      eventsScrollRef.current.scrollTo({
        y: position || position === 0 ? position : scrollPosition.current + 300,
        animated: true
      })
    }
  }, [])

  const onChangeDate = useCallback((newDate) => {
    const filterEvents = (date) => {
      const newDateEvents = events.find((item) => item.date === date.format('YYYY/MM/DD'))
      setFilteredEvents(newDateEvents ? newDateEvents.events : [])
      onScrollDown(0)
    }

    if (newDate) {
      let date = ''
      if (newDate.timestamp) {
        date = moment.utc(newDate.timestamp)
      } else {
        date = moment(newDate, 'YYYY-MM-DD')
      }
      setSelectedDate(date)
      filterEvents(date)
    }
  }, [events, onScrollDown])

  useEffect(() => {
    onChangeDate(moment.utc())
  }, [events, onChangeDate])

  const onEventsListSizeChange = useCallback((width, height) => {
    if (isDesktop) {
      const showFade = height > eventsListMaxHeight
      Animated.timing(
        fadeAnim,
        {
          toValue: showFade ? 1 : 0,
          duration: 500
        }
      ).start()
    }
  }, [isDesktop, fadeAnim])

  const onEventsListScroll = useCallback((event) => {
    scrollPosition.current = event.nativeEvent.contentOffset.y
  }, [])

  const skeleton = {
    container: {
      flex: 1,
      flexDirection: loading ? 'row' : 'column'
    },
    calendar: {
      flex: 1,
      height: isDesktop ? 288 : 122,
      paddingVertical: 10
    },
    event: {
      flex: 1,
      height: 55,
      paddingBottom: isDesktop ? 20 : 10
    }
  }

  return (
    <View style={styles.container}>
      <View style={styles.calendar}>
        <Txt
          text='Calendar'
          identity='body1Bold'
          color='OUTER_SPACE'
        />
        <SkeletonContent
          containerStyle={loading ? skeleton.container : null}
          isLoading={loading}
          layout={[
            skeleton.calendar
          ]}
        >
          <Calendar
            events={events}
            weekView={!isDesktop}
            onPressView={() => setOpenEventsModal(true)}
            currentDate={selectedDate.format('YYYY-MM-DD')}
            onChangeDate={onChangeDate}
          />
        </SkeletonContent>
      </View>
      <View style={styles.orders}>
        <Txt
          text={`My Orders${filteredEvents.length ? ' (' + filteredEvents.length + ')' : ''}`}
          identity='body1Bold'
          color='OUTER_SPACE'
        />
        <SkeletonContent
          containerStyle={loading ? skeleton.container : null}
          isLoading={loading}
          layout={[
            skeleton.event
          ]}
        >
          <View style={styles.events}>
            <ScrollView
              ref={eventsScrollRef}
              noScroll={isDesktop}
              style={{
                marginBottom: isDesktop ? 0 : 20,
                height: isDesktop ? eventsListMaxHeight : 'auto'
              }}
              onContentSizeChange={onEventsListSizeChange}
              onScroll={onEventsListScroll}
            >
              {filteredEvents.length ? (
                <FlatList
                  data={filteredEvents}
                  numColumns={1}
                  renderItem={({ item }) => <EventCard event={item} />}
                  keyExtractor={(item, key) => key.toString()}
                />
              ) : (
                <Txt
                  text={`No events found on ${selectedDate.format('MMMM Do')}`}
                  identity='body2'
                  color='GEYSER'
                  style={styles.noEventsMessage}
                />
              )}
            </ScrollView>
            <Animated.View
              style={[
                styles.eventsFade,
                { opacity: fadeAnim }
              ]}
            >
              <LinearGradient
                colors={['transparent', theme.WHITE]}
                style={styles.gradient}
              />
            </Animated.View>
            <Animated.View
              style={[
                styles.scrollIcon,
                { opacity: fadeAnim }
              ]}
            >
              <IconButton
                icon='DoubleArrowDown'
                onPress={() => onScrollDown()}
                color='AZTEC'
              />
            </Animated.View>
          </View>
        </SkeletonContent>
      </View>
      <EventsModal
        events={events}
        filteredEvents={filteredEvents}
        date={selectedDate.format('YYYY-MM-DD')}
        open={openEventsModal}
        onClose={() => setOpenEventsModal(false)}
        onChangeDate={onChangeDate}
      />
    </View>
  )
}

const useStyles = (isDesktop) => StyleSheet.create({
  noEventsMessage: {
    marginVertical: 10
  },
  container: {
    flexDirection: isDesktop ? 'row' : 'column'
  },
  calendar: {
    width: isDesktop ? 370 : 'auto',
    marginRight: isDesktop ? 10 : 0
  },
  orders: {
    flex: 1,
    marginLeft: isDesktop ? 20 : 0
  },
  events: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  eventsFade: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    height: 20
  },
  scrollIcon: {
    position: 'absolute',
    bottom: 10,
    right: 10
  },
  gradient: {
    height: '100%',
    flex: 1,
    borderBottomLeftRadius: 5,
    borderBottomRightRadius: 5
  }
})

EventsSection.propTypes = {
  events: PropTypes.array.isRequired,
  loading: PropTypes.bool
}

EventsSection.defaultProps = {
  events: [],
  loading: false
}

export default EventsSection
